// Who may see voicemail, act on it and manage greetings. Kept tiny: the navigation imports it on every page.
//
// Seam (same as routing/access.ts): packages/domain ROLES doesn't list voicemail permissions, so the session's
// `permissions` carries them only as explicit grants. The API merges role defaults in
// apps/api/src/voicemail/permissions.ts; this mirrors that matrix from the session's own roles. The server stays
// the authority: a 403 turns the inbox denied, and a 403 on a change turns that change off.
import type { Session } from '@bcio-voice/contracts';
import { sessionRoles } from '../routing/access.ts';

export type VoicemailPermission = 'voicemails:read' | 'voicemails:read_all' | 'voicemails:manage' | 'greetings:manage';

/** Same matrix as VOICEMAIL_ROLE_PERMISSIONS in apps/api/src/voicemail/permissions.ts. */
export const VOICEMAIL_ROLE_DEFAULTS: Readonly<Record<string, readonly VoicemailPermission[]>> = Object.freeze({
  owner: ['voicemails:read', 'voicemails:read_all', 'voicemails:manage', 'greetings:manage'],
  admin: ['voicemails:read', 'voicemails:read_all', 'voicemails:manage', 'greetings:manage'],
  supervisor: ['voicemails:read', 'voicemails:read_all', 'voicemails:manage'],
  agent: ['voicemails:read', 'voicemails:manage'],
  analyst: [],
});

export type VoicemailAccess =
  | { kind: 'no_tenant' }
  | { kind: 'denied' }
  | { kind: 'allowed'; scope: 'all' | 'own'; canManage: boolean };

export function voicemailPermissions(session: Pick<Session, 'tenant' | 'memberships' | 'permissions'>): Set<string> {
  return new Set([...session.permissions, ...sessionRoles(session).flatMap(role => (Object.hasOwn(VOICEMAIL_ROLE_DEFAULTS, role) ? VOICEMAIL_ROLE_DEFAULTS[role] ?? [] : []))]);
}

/** The inbox: visible with voicemails:read (own numbers) or voicemails:read_all. */
export function voicemailAccess(session: Pick<Session, 'user' | 'tenant' | 'memberships' | 'permissions'>): VoicemailAccess {
  if (!session.user || !session.tenant) return { kind: 'no_tenant' };
  const permissions = voicemailPermissions(session);
  const scope = permissions.has('voicemails:read_all') ? 'all' : permissions.has('voicemails:read') ? 'own' : null;
  if (!scope) return { kind: 'denied' };
  return { kind: 'allowed', scope, canManage: permissions.has('voicemails:manage') };
}

/** Uploading, replacing and removing number greetings (reading them needs routing:read, which the routing page already has). */
export function canManageGreetings(session: Pick<Session, 'tenant' | 'memberships' | 'permissions'>): boolean {
  return voicemailPermissions(session).has('greetings:manage');
}
